"use client";

import { Button } from "@repo/ui/components/ui/button";
import { Skeleton } from "@repo/ui/components/ui/skeleton";
import { Field, FieldLabel } from "@repo/ui/form-fields";
import { cn } from "@repo/ui/lib/utils";
import { useFieldContext } from "@/shared/forms/form-context";

/**
 * Structural copy of scheduling's TimeSlot. Declared here rather than imported
 * because shared/ may not depend on a feature, and this field is registered in
 * the form kit that every feature uses. Structural typing keeps the two
 * compatible without an edge.
 */
export interface TimeSlot {
  startTime: string;
  endTime: string;
  dateDisplay: string;
  display: string;
  endDisplay: string;
}

export interface AvailabilitySlotPickerProps {
  label?: string;
  slots: TimeSlot[];
  isLoading: boolean;
  error: string | null;
  onSlotSelect?: (slot: TimeSlot) => void;
}

export function AvailabilitySlotPicker({ label, slots, isLoading, error, onSlotSelect }: AvailabilitySlotPickerProps) {
  const field = useFieldContext<string>();

  return (
    <Field>
      {label && <FieldLabel>{label}</FieldLabel>}

      {isLoading && (
        <div className="flex flex-col gap-2">
          {["a", "b", "c", "d", "e"].map((id) => (
            <Skeleton key={id} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      )}

      {!isLoading && error && <p className="text-sm text-destructive">{error}</p>}

      {!isLoading && !error && slots.length === 0 && (
        <p className="text-sm text-muted-foreground">No available times for this date</p>
      )}

      {!isLoading && !error && slots.length > 0 && (
        <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
          {slots.map((slot) => {
            const selected = field.state.value === slot.startTime;
            return (
              <Button
                key={slot.startTime}
                type="button"
                variant={selected ? "default" : "outline"}
                className={cn("h-auto w-full flex-col items-start gap-0 py-2")}
                onClick={() => {
                  field.handleChange(slot.startTime);
                  field.handleBlur();
                  onSlotSelect?.(slot);
                }}
              >
                <span className={cn("text-xs", selected ? "text-primary-foreground/70" : "text-muted-foreground")}>
                  {slot.dateDisplay}
                </span>
                <span className="text-sm font-medium">
                  {slot.display} – {slot.endDisplay}
                </span>
              </Button>
            );
          })}
        </div>
      )}
    </Field>
  );
}
